/**
 * Helper functions for event formatting and data manipulation
 */

/**
 * Format date into localized string format
 * @param {string} dateStr - Date string to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateStr) => {
    if (!dateStr) return 'Datum ni določen';
    const date = new Date(dateStr);
    return date.toLocaleDateString('sl-SI', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  /**
   * Format time to remove seconds
   * @param {string} timeStr - Time string to format
   * @returns {string} Formatted time without seconds
   */
  export const formatTime = (timeStr) => {
    if (!timeStr) return '';
    
    // If the time already doesn't have seconds, return as is
    if (!timeStr.includes(':')) return timeStr;
    
    // Check if time has the format HH:MM:SS
    const timeParts = timeStr.split(':');
    if (timeParts.length === 3) {
      // Return only hours and minutes (HH:MM)
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    
    // Return original time if it doesn't match expected format
    return timeStr;
  };
  
  /**
   * Format visibility for display
   * @param {string} visibility - Visibility value
   * @returns {string} Formatted visibility text
   */
  export const formatVisibility = (visibility) => {
    if (!visibility) return 'Privzeto';
    
    switch(visibility.toLowerCase()) {
      case 'public':
        return 'Javno';
      case 'private':
        return 'Zasebno';
      case 'community':
        return 'Skupina';
      default:
        return visibility;
    }
  };
  
  /**
   * Get gradient background based on event ID
   * @param {object} event - Event object with ID
   * @returns {string} CSS gradient string
   */
  export const getGradient = (event) => {
    // List of gradients similar to your EventCard component
    const EVENT_GRADIENTS = [
      'linear-gradient(135deg, #355C7D, #6C5B7B)',
      'linear-gradient(135deg, #414345, #232526)',
      'linear-gradient(135deg, #283048, #859398)',
      'linear-gradient(135deg, #373B44, #4286f4)',
      'linear-gradient(135deg, #200122, #6f0000)',
      'linear-gradient(135deg, #5433FF, #20BDFF)',
      'linear-gradient(135deg, #2C3E50, #4CA1AF)',
      'linear-gradient(135deg, #4568DC, #B06AB3)',
      'linear-gradient(135deg, #41295a, #2F0743)',
      'linear-gradient(135deg, #093028, #237A57)',
      'linear-gradient(135deg, #6441A5, #2a0845)',
      'linear-gradient(135deg, #42275a, #734b6d)',
      'linear-gradient(135deg, #141E30, #243B55)',
      'linear-gradient(135deg, #870000, #190A05)',
      'linear-gradient(135deg, #232526, #414345)'
    ];
  
    if (!event?.id) {
      const randomIndex = Math.floor(Math.random() * EVENT_GRADIENTS.length);
      return EVENT_GRADIENTS[randomIndex];
    }
    
    const numericId = typeof event.id === 'string' ? 
      parseInt(event.id.replace(/[^0-9]/g, ''), 10) : 
      Number(event.id);
    
    const index = numericId % EVENT_GRADIENTS.length;
    return EVENT_GRADIENTS[index];
  };
  
  /**
   * Check if a song has a part file
   * @param {string} songId - Song ID to check
   * @param {object} songsWithParts - Map of songs with part information
   * @returns {boolean} True if song has part file
   */
  export const hasPart = (songId, songsWithParts) => {
    if (!songId) return false;
    const fullSong = songsWithParts[songId];
    return fullSong && fullSong.has_part_file;
  };
  
  /**
   * Check if a song has a comment
   * @param {object} song - Song object to check
   * @returns {boolean} True if song has a comment
   */
  export const hasComment = (song) => {
    return song && song.comment && song.comment.trim() !== '';
  };
  
  /**
   * Helper function to determine if user can edit an event
   * @param {object} event - Event object
   * @param {object} user - Current user object
   * @param {array} userCommunities - User's communities
   * @param {function} canEditCommunityContent - Function to check community edit permissions
   * @returns {boolean} True if user can edit the event
   */
  export const checkEventEditPermission = (event, user, userCommunities, canEditCommunityContent) => {
    // If no event data or no user, can't edit
    if (!event || !user) {
      return false;
    }
    
    // Global admins can edit anything
    if (user.is_global_admin) {
      return true;
    }
    
    // Event owners can edit their own events
    if (user.id === event.owner_id) {
      return true;
    }
    
    // If event is in a community, check community permissions
    if (event.community_id) {
      // Community admins can always edit
      if (user.isAdminOfCommunity && user.isAdminOfCommunity(event.community_id)) {
        return true;
      }
      
      // For regular members, check if the community allows member edits
      const hasEditPermission = canEditCommunityContent(event.community_id, {
        isGlobalAdmin: user.is_global_admin,
        isAdminOfCommunity: (communityId) => 
          user.isAdminOfCommunity && user.isAdminOfCommunity(communityId),
        userCommunities: userCommunities
      });
      
      return hasEditPermission;
    }
    
    // Default: no edit permission
    return false;
  };